import { useEffect, useState } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';

/**
 * What the planner is doing, while it does it.
 *
 * A route takes seconds on an easy day and most of a minute on one that
 * avoids half a city, and all the map said was a two-pixel bar at its top
 * edge — easy to miss entirely, and silent about whether anything was
 * happening at all. This says so plainly, and keeps saying it.
 *
 * Deliberately NOT a step-by-step readout of the server's own stages. The
 * plan is one request with no progress channel, so any "now solving…" here
 * would be a guess dressed as a measurement — and a guess that says
 * "optimising" while the engine is still building its matrix is worse than
 * no detail at all. What is shown instead is true: that it is working, how
 * long it has been, and why this particular round is slow.
 */
export function PlanningProgress({
  open, avoidCount, what = 'route',
}: {
  open: boolean;
  /**
   * How many areas this round is keeping clear of.
   *
   * Worth knowing because it is the usual answer to "why is this taking so
   * long": exclusions put the routing engine off its precomputed shortcuts,
   * and the search then expands around every blocked edge. Measured at about
   * fifteen times the cost of the same round without them.
   */
  avoidCount?: number;
  /** "route" on one day, "days" for an operation. */
  what?: string;
}) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!open) {
      setSeconds(0);

      return;
    }

    const started = Date.now();
    const tick = window.setInterval(
      () => setSeconds(Math.floor((Date.now() - started) / 1000)),
      1000,
    );

    return () => window.clearInterval(tick);
  }, [open]);

  if (!open) return null;

  /*
    One line of context, chosen by what is true rather than by a timeline.

    The avoidance note comes first because it is the actual reason a slow
    round is slow. The patience line only appears once the wait is long
    enough to worry about, which on an ordinary round never happens.
  */
  const note = (avoidCount ?? 0) > 0
    ? `Holding clear of ${avoidCount} area${avoidCount === 1 ? '' : 's'}, `
      + 'which makes the roads harder to search.'
    : seconds >= 12
      ? 'Still working — a busy city takes longer to weigh up.'
      : '';

  return (
    <Paper
      elevation={8}
      sx={{
        /*
          Fixed to the window, not to the map.

          It started inside the map container, which is the wrong place on a
          narrow screen: the form and the map take turns there, so while
          somebody watches the form — which is exactly when they have just
          pressed Plan — the map is not laid out and the card went with it.

          Low enough to clear the header and the mode tabs. At the very top
          it was cut in half by them, which is how a loading indicator comes
          to look broken at the moment it is meant to reassure.
        */
        position: 'fixed',
        top: 96,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: (theme) => theme.zIndex.drawer + 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.75,
        pl: 2, pr: 2.5, py: 1.5,
        maxWidth: 'calc(100% - 32px)',
        borderRadius: 999,
        border: 1,
        borderColor: 'divider',
        /*
          A pill rather than a panel with a bar across its foot.

          The bar was a second animation saying what the spinner already
          said, and being full width it read as part of the map's chrome
          rather than part of this. One moving thing is enough.
        */
      }}
    >
      <CircularProgress size={22} thickness={4.5} />

      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
          {`Calculating ${what}…`}
          {/* The count is the one honest measure of progress available. */}
          {seconds > 2 && (
            <Box component="span" sx={{ fontWeight: 400, color: 'text.secondary' }}>
              {` ${seconds}s`}
            </Box>
          )}
        </Typography>
        {note && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', lineHeight: 1.35 }}
          >
            {note}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}
